import { useState } from 'react';
import { Check, Trash2, Sparkles } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { CertificatePreview, defaultTemplates, type CertificateTemplate } from './CertificatePreview';

interface CertificateTemplateSelectorProps {
  value?: string;
  onChange: (template: CertificateTemplate) => void;
  templates?: CertificateTemplate[];
  onDelete?: (templateId: string) => void;
}

const sampleData = {
  recipientName: 'Jane Smith',
  pathwayName: 'Sales Excellence Program',
  certificationName: 'Certified Sales Professional',
  completionDate: new Date(),
  score: 92,
  companyName: 'MEDD Training',
  issuerName: 'Director of Training'
};

export function CertificateTemplateSelector({ value, onChange, templates, onDelete }: CertificateTemplateSelectorProps) {
  const [selectedId, setSelectedId] = useState(value || 'classic');
  
  const allTemplates = templates || defaultTemplates;
  const builtInTemplates = allTemplates.filter(t => !t.isCustom);
  const customTemplates = allTemplates.filter(t => t.isCustom);

  const handleSelect = (template: CertificateTemplate) => {
    setSelectedId(template.id);
    onChange(template);
  };

  const renderTemplateCard = (template: CertificateTemplate, showDelete = false) => (
    <Card
      key={template.id}
      className={cn(
        'cursor-pointer transition-all hover:shadow-lg hover:-translate-y-1 relative overflow-hidden group',
        selectedId === template.id && 'ring-2 ring-primary ring-offset-2'
      )}
      onClick={() => handleSelect(template)}
    >
      <CardContent className="p-3">
        {/* Preview Thumbnail */}
        <div className="aspect-[1.414] bg-muted rounded overflow-hidden mb-3 relative">
          <div className="absolute inset-0 scale-[0.2] origin-top-left">
            <CertificatePreview
              template={template}
              data={{ ...sampleData, companyName: template.companyName || sampleData.companyName }}
              className="w-[500px]"
            />
          </div>
          
          {/* Overlay on hover */}
          <div className="absolute inset-0 bg-background/80 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
            <span className="text-xs font-medium">Click to select</span>
          </div>

          {/* Custom badge */}
          {template.isCustom && (
            <div className="absolute top-1 right-1 z-10">
              <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                <Sparkles className="w-2.5 h-2.5 mr-0.5" />
                Custom
              </Badge>
            </div>
          )}
        </div>

        {/* Template Name */}
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium truncate">{template.name}</span>
          <div className="flex items-center gap-1">
            {showDelete && onDelete && (
              <Button
                variant="ghost"
                size="sm"
                className="h-5 w-5 p-0 opacity-0 group-hover:opacity-100 transition-opacity text-destructive hover:text-destructive"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(template.id);
                }}
              >
                <Trash2 className="w-3 h-3" />
              </Button>
            )}
            {selectedId === template.id && (
              <div className="w-5 h-5 rounded-full bg-primary flex items-center justify-center">
                <Check className="w-3 h-3 text-primary-foreground" />
              </div>
            )}
          </div>
        </div>

        {/* Style indicator */}
        <div className="flex items-center gap-1 mt-1">
          <div 
            className="w-3 h-3 rounded-full"
            style={{ backgroundColor: template.primaryColor }}
          />
          <div 
            className="w-3 h-3 rounded-full"
            style={{ backgroundColor: template.accentColor }}
          />
          <span className="text-xs text-muted-foreground ml-1 capitalize">
            {template.fontFamily}
          </span>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6">
      {/* Default Templates */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">Default Templates</h3>
          <Badge variant="secondary">
            {builtInTemplates.length} templates
          </Badge>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {builtInTemplates.map((template) => renderTemplateCard(template))}
        </div>
      </div>

      {/* Custom Templates */}
      {customTemplates.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-primary" />
              Your Custom Templates
            </h3>
            <Badge variant="secondary">
              {customTemplates.length} saved
            </Badge>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {customTemplates.map((template) => renderTemplateCard(template, true))}
          </div>
        </div>
      )}

      {/* Selected Preview */}
      {selectedId && (
        <Card className="mt-6">
          <CardContent className="p-6">
            <h4 className="text-sm font-medium mb-4">Preview</h4>
            <div className="flex justify-center">
              <div className="w-full max-w-lg">
                <CertificatePreview
                  template={allTemplates.find(t => t.id === selectedId) || defaultTemplates[0]}
                  data={sampleData}
                />
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
